// Ported from dev-scrips-pm-react src/documents/components/new-file-popup.tsx (PROD-1119 documents module).
import { useEffect, useId, useMemo, useRef, useState } from 'react';
// GAP: no DS icon for procedure (Stethoscope, K3/O8) or spreadsheet / admin & referrals (FileSpreadsheet); lucide in both modes (design sheet §3.1, logged not substituted).
import {
  Check,
  ClipboardList,
  Download,
  FileSpreadsheet,
  FileText,
  FlaskConical,
  Pencil,
  Plus,
  Scan,
  Stethoscope,
  Trash2,
  X,
  type LucideIcon,
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
// DS reconciliation (PROD-1119): shadcn select → DS Select (per
// shadcn→DS mapping §3). The compound `<Select><SelectTrigger>
// <SelectValue/></SelectTrigger><SelectContent><SelectItem/>…` tree
// collapses to a single `<Select options=[] value onChange placeholder>`.
// Drawer migration (2026-09-20): replaces the local shadcn Sheet. Drawer is
// props-based (open/onClose/title/headerLeading/headerActions/...), not
// composed subcomponents — see the call site below for the restructure.
import { DatePicker, Drawer, Select, BrandLoading } from '@scripsteam/scrips-signal-ds';
import { DropZone } from '@/shared/ui/components/drop-zone';
import { dateToYmd, ymdToLocalDate } from '@/shared/lib/local-date';
import { useSaveDocument } from '@/documents/services/use-save-document';
import { useUploadDocument } from '@/documents/services/use-upload-document';
import { useOrganizationForms } from '@/documents/services/use-organization-forms';
import { useConsentTemplates } from '@/consent/services';
import {
  isDocumentTemplate,
  type ConsentTemplate,
} from '@/consent/types/consent-template-api';
import { DocumentType } from '@/documents/types/document';
import {
  DEFAULT_DOCUMENT_MAX_UPLOAD_MB,
  DOCUMENT_ACCEPT_ATTR,
  TOAST_DOCUMENT_FILE_INVALID,
  TOAST_UPLOAD_LIMIT_MISSING,
  isFileSizeGreater,
  isMimeConsistent,
  isValidExtension,
  oversizeMessage,
} from '@/documents/lib/file-allowlist';
import { useAuthStore } from '@/auth/auth-store';
import {
  LabNamePicker,
  UnitPicker,
} from '@/documents/components/lab-row-editor';
import { CodePicker } from '@/documents/components/code-picker';
import type {
  DocumentTypeCode,
  PatientDocumentData,
  PatientDocumentFile,
  PatientDocumentLab,
  PatientDocumentModel,
} from '@/documents/types/document';
// PROD-2743 design sheet §3 (icons): DS names ON only (?ef=frameV2); OFF keeps today's lucide icon, unchanged.

/**
 * NewFilePopup — Add-document side sheet (PROD-797 PR 2 of 6).
 *
 * Mirrors Flutter `new_file_popup_widget.dart` + the simpler
 * sub-form variants (Notes / Form / Admin Referrals). Lab / Procedure
 * / Radiology sub-forms ship in PRs 3-4; this popup hides their
 * options from the type picker for V1.
 *
 * Flow for the THREE TYPES this PR supports:
 *
 *   Notes / Admin Referrals (identical UX):
 *     1. Pick type
 *     2. Name (required) + Document date (required, defaults today)
 *     3. Optional metadata: doctor name, facility name, tags
 *     4. Attach 1+ files (drag-drop or browse)
 *     5. Save → upload each file → AddUpdate with collected URLs
 *
 *   Form:
 *     1. Pick type = Form
 *     2. Dropdown: org form templates (useOrganizationForms)
 *     3. Download link for the picked template (blank PDF / DOC for
 *        the user to fill offline)
 *     4. Document date (required) + optional metadata
 *     5. Attach the filled file
 *     6. Save → upload → AddUpdate
 *
 * Two-step submit:
 *   a) Upload each File via useUploadDocument → collect signed URLs
 *   b) Build PatientDocumentModel with patientDocumentFiles + ship
 *      via useSaveDocument
 *
 * `data: { title: null, code: null }` ships verbatim on every save
 * even when unused — per the dev curl shape (PROD-797 PR 1 contract).
 */

interface NewFilePopupProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  patientId: string;
  /** Optional encounter scope; ships through to AddUpdate as-is. */
  encounterId?: string | null;
  /**
   * Lock the doc-type picker to a single type. When set, the type-tabs
   * row is hidden and the popup starts on (and stays on) the locked
   * type. Used by the Lab Linkage Order Requests tab to reuse this
   * shared popup for the result-upload flow but constrained to the
   * Labs tab (Andrew direction 2026-05-21).
   */
  lockedType?: DocumentTypeCode;
  /**
   * Optional callback fired AFTER `useSaveDocument` succeeds and BEFORE
   * the popup closes. The save response is the new document id as a
   * bare string (curl-4 wire shape, verified 2026-05-21). Consumers
   * chain follow-up mutations here (e.g. lab-order linkage via
   * `useLinkLabResult`).
   *
   * If `onSaved` throws, the popup STAYS OPEN — same retry-friendly
   * pattern as the upload-failure path. The caller is expected to
   * have surfaced an error toast.
   */
  onSaved?: (documentId: string) => Promise<void> | void;
  /**
   * PROD-899 — when set, the popup opens in EDIT mode pre-populated
   * from this row. Save dispatches the same
   * `POST /api/Patients/AddUpdatePatientDocument` with `id` populated
   * so the backend treats it as an upsert.
   *
   * Hydrated state covers every editable field — name, dates, doctor
   * / facility / tags, code picker (Procedure / Radiology), lab rows,
   * and the document's currently-attached files. Server-set audit
   * fields (`createdById`, `reviewedOn`, etc.) ride along verbatim
   * via spread so the round-trip preserves review state.
   *
   * In edit mode the type-tabs row is hidden — switching type
   * mid-edit isn't supported in Flutter and breaks code-driven
   * forms; the existing row's type stays put.
   */
  initialDocument?: PatientDocumentModel;
  /**
   * PROD-1051 doc-forms — route by template mode. When the user picks a published
   * **document-mode** consent template in the Form flow, this fires instead of the
   * download-blank-and-reupload path; the parent opens the in-app fill-and-sign popup.
   * HTML / legacy org-form templates keep the existing offline download flow.
   */
  onPickConsentTemplate?: (template: ConsentTemplate) => void;
  /**
   * PROD-1243 (Case 3) — the consent was signed OUTSIDE the app. When the caller supports
   * it, the Form-template picker shows a "Sign in app / Upload signed PDF" toggle; picking a
   * template in upload mode fires this (instead of `onPickConsentTemplate`) so the parent
   * opens the external-upload dialog. Omit to hide the toggle (in-app fill only).
   */
  onUploadSignedConsent?: (template: ConsentTemplate) => void;
}

interface FileRow {
  /** Local id so React keys don't desync if a user removes mid-upload. */
  localId: string;
  /**
   * The `File` instance to upload. When the user renames the file in
   * the picker (PROD-903), we rebuild this with `new File([file],
   * newName, {type, lastModified})` so `file.name` reflects the new
   * value AND the upload's multipart `filename=` header ships the
   * renamed value.
   */
  file: File;
}

/**
 * Format a date as Flutter's `scDateFormat` default (`'MMM dd, yyyy'`,
 * e.g. `"May 23, 2026"`). Used by the Lab-name auto-default (PROD-904).
 * Mirrors Flutter `scrips_core/lib/utils/utils.dart:176`.
 */
function formatScDate(d: Date): string {
  // Intl gives a stable "May 23, 2026" without locale-specific punctuation.
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: '2-digit',
    year: 'numeric',
  }).format(d);
}

/** Strip extension from a filename (e.g. "report.pdf" → "report"). */
function stripExtension(filename: string): string {
  const idx = filename.lastIndexOf('.');
  return idx > 0 ? filename.slice(0, idx) : filename;
}

/**
 * Doc-type tabs row at the top of the popup. Order + icons mirror the
 * Flutter "New File" widget: Note · Form · Lab · Radiology · Procedure ·
 * Admin & Referrals. Icons match the row-action icons in `document-row.tsx`
 * (same `iconFor` map) so the popup tab + the resulting list row share
 * one visual identity.
 */
const TYPE_OPTIONS_V1: {
  value: DocumentTypeCode;
  label: string;
  icon: LucideIcon;
}[] = [
  { value: DocumentType.Notes, label: 'Note', icon: FileText },
  { value: DocumentType.Forms, label: 'Form', icon: ClipboardList },
  { value: DocumentType.Labs, label: 'Lab', icon: FlaskConical },
  { value: DocumentType.Radiology, label: 'Radiology', icon: Scan },
  { value: DocumentType.Procedure, label: 'Procedure', icon: Stethoscope },
  {
    value: DocumentType.AdminReferrals,
    label: 'Admin & Referrals',
    icon: FileSpreadsheet,
  },
];

/**
 * Document types whose `name` field is sourced from the picked code's
 * display string (NOT a free-text input). Used by the validation +
 * submit-body logic.
 */
const CODE_DRIVEN_TYPES: DocumentTypeCode[] = [
  DocumentType.Procedure,
  DocumentType.Radiology,
];

/** Build an empty lab row template — local-only id for React keys. */
function newLabRow(): PatientDocumentLab {
  return {
    id: null,
    patientDocumentId: null,
    labName: null,
    result: '',
    unitType: null,
    isDeleted: false,
  };
}

/**
 * Per-row React key (Samer review PR #139 §2). Generated ONCE at row
 * creation via `crypto.randomUUID()` and never reassigned, so adding /
 * removing rows mid-list doesn't make React reuse the wrong editor
 * state. NOT array-index-based — those would collide when a middle
 * row is removed and the trailing rows shift down.
 *
 * Falls back to a counter for the (vanishingly small) chance the
 * runtime doesn't expose `crypto.randomUUID` — every modern browser
 * does, including Safari 15.4+.
 */
let __labRowKeyFallbackSeq = 0;
function nextLabRowKey(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  __labRowKeyFallbackSeq += 1;
  return `lab-row-fallback-${__labRowKeyFallbackSeq}`;
}

function todayLocalIso(): string {
  // `YYYY-MM-DDTHH:mm:ss.sss` — no Z (matches the dev-curl wire
  // contract). Built off Date components instead of toISOString().
  const d = new Date();
  const pad = (n: number, w = 2) => String(n).padStart(w, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours(),
  )}:${pad(d.getMinutes())}:${pad(d.getSeconds())}.${pad(d.getMilliseconds(), 3)}`;
}

function dateInputToWire(value: string): string {
  // <input type="date"> gives `YYYY-MM-DD`; append a midnight local
  // time + millis to match the wire shape (`...T00:00:00.000`).
  return value ? `${value}T00:00:00.000` : '';
}

function wireToDateInput(value: string | null | undefined): string {
  if (!value) return '';
  return value.slice(0, 10);
}

function extensionFor(filename: string): string {
  const idx = filename.lastIndexOf('.');
  return idx >= 0 ? filename.slice(idx).toLowerCase() : '';
}

/**
 * Force-download a cross-origin URL with the desired filename. The
 * standard `<a download>` attribute is silently ignored for
 * cross-origin hrefs (Azure-blob URLs always are), so we fetch the
 * resource ourselves, wrap it in an ObjectURL, and synthesise an
 * anchor click. Falls back to opening in a new tab if the fetch
 * fails (CORS, expired SAS, network).
 */
async function downloadCrossOrigin(url: string, filename: string): Promise<void> {
  try {
    const res = await fetch(url);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const blob = await res.blob();
    const objectUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = objectUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    // Revoke on next tick so the click handler can still consume the
    // ObjectURL — revoking synchronously cancels the download in Safari.
    setTimeout(() => URL.revokeObjectURL(objectUrl), 0);
  } catch {
    // Fallback: open the original URL in a new tab. The user can
    // right-click → save from there if the browser renders inline.
    window.open(url, '_blank', 'noopener,noreferrer');
  }
}

/**
 * Resolve a downloadable URL off an org form-template row.
 * `/api/Patients/OrganizationForm` ships the SAS-signed blob URL on
 * **`formPath`** (curl-verified 2026-06-10 — see
 * `use-organization-forms.ts`). The remaining names are kept as
 * defensive fallbacks in case a future backend rename lands.
 */
function resolveFormTemplateUrl(
  f: Record<string, unknown> | undefined | null,
): string | null {
  if (!f) return null;
  for (const k of ['formPath', 'url', 'fileUrl', 'documentUrl', 'path']) {
    const v = f[k];
    if (typeof v === 'string' && v.length > 0) return v;
  }
  return null;
}

/**
 * Download filename for a form template. The wire has no filename
 * field, so we derive it from the `formPath` blob path (which carries
 * the real `*.pdf` name), stripping the SAS query string. Falls back
 * to `formName` when the path can't be parsed.
 */
function resolveFormTemplateFilename(
  f: Record<string, unknown> | undefined | null,
): string {
  if (f) {
    const url = resolveFormTemplateUrl(f);
    if (url) {
      try {
        const pathname = new URL(url).pathname;
        const last = decodeURIComponent(
          pathname.slice(pathname.lastIndexOf('/') + 1),
        );
        if (last) return last;
      } catch {
        // Not a parseable absolute URL — fall through to the name fields.
      }
    }
    for (const k of ['fileName', 'formName', 'name']) {
      const v = f[k];
      if (typeof v === 'string' && v.length > 0) return v;
    }
  }
  return 'template';
}

/**
 * PROD-1051 doc-forms — the "Form template" dropdown lists published document-mode consent
 * templates; picking one routes to the parent's in-app fill-and-sign popup (no offline
 * download). This REPLACES the legacy org-form-template values in this dropdown for any
 * caller that supports fill-and-sign (Andrew 2026-06-17). Isolated in its own component so
 * `useConsentTemplates` only runs when the Form type is active AND the parent opted in.
 *
 * Ported from dev-scrips-pm-react `new-file-popup.tsx` (PROD-1724), with PM's compound
 * shadcn `<Select><SelectTrigger><SelectValue>…` tree collapsed to the DS `Select` this
 * file already uses for the org-forms picker — same prop shape (`options`/`value`/`onChange`),
 * and the DS renders the label by value, so PM's base-ui `SelectValue` id→label function
 * child is not needed here.
 */
function InAppConsentFormPicker({
  onPick,
  onUploadExternal,
}: {
  onPick: (t: ConsentTemplate) => void;
  /** When provided, a "Sign in app / Upload signed PDF" toggle appears; upload mode routes here. */
  onUploadExternal?: (t: ConsentTemplate) => void;
}) {
  const { data, isLoading, isError } = useConsentTemplates();
  const [mode, setMode] = useState<'in-app' | 'external'>('in-app');
  const signable = (data ?? []).filter(
    (t) => t.status === 'published' && isDocumentTemplate(t),
  );
  const external = mode === 'external' && !!onUploadExternal;
  return (
    <div className="space-y-2">
      <label className="text-xs font-bold uppercase tracking-wide text-text-muted">
        Form template
      </label>
      {onUploadExternal ? (
        // PROD-1243: choose how the form is signed before picking the template.
        <div className="inline-flex rounded-md bg-grey-100 p-1">
          {(['in-app', 'external'] as const).map((m) => (
            <button
              key={m}
              type="button"
              aria-pressed={mode === m}
              onClick={() => setMode(m)}
              className={cn(
                'h-8 px-3 rounded-md text-size-compact font-medium transition-colors',
                mode === m
                  ? 'bg-surface-card text-text-primary shadow-sm'
                  : 'text-text-secondary hover:text-text-primary',
              )}
            >
              {m === 'in-app' ? 'Sign in app' : 'Upload signed PDF'}
            </button>
          ))}
        </div>
      ) : null}
      {isLoading ? (
        <BrandLoading size={48} label="Loading templates…" />
      ) : isError ? (
        <p className="text-sm text-status-error">Couldn't load form templates.</p>
      ) : signable.length === 0 ? (
        <p className="text-sm text-text-muted">
          No form templates configured for this organization.
        </p>
      ) : (
        // value="" — picking a template immediately routes (this popup closes), so there's
        // no persistent selection to track here.
        <Select
          className="w-full"
          value=""
          placeholder={external ? 'Pick the form that was signed…' : 'Pick a form template…'}
          options={signable.map((t) => ({ label: t.name, value: t.id }))}
          onChange={(v) => {
            const t = signable.find((x) => x.id === v);
            if (!t) return;
            if (external) onUploadExternal!(t);
            else onPick(t);
          }}
        />
      )}
    </div>
  );
}

export function NewFilePopup({
  open,
  onOpenChange,
  patientId,
  encounterId,
  lockedType,
  onSaved,
  initialDocument,
  onPickConsentTemplate,
  onUploadSignedConsent,
}: NewFilePopupProps) {
  // PROD-899: edit mode is signalled by `initialDocument` being set.
  // The popup is then pre-populated from the row and Save dispatches
  // an upsert with the existing `id`. Type-switching is disabled
  // (Flutter parity — the existing row's type is what gets saved).
  const isEditMode = !!initialDocument;
  const [docType, setDocType] = useState<DocumentTypeCode>(
    (initialDocument?.type as DocumentTypeCode | undefined) ??
      lockedType ??
      DocumentType.Notes,
  );
  // Refs for the file-type tab buttons so the arrow-key roving handler
  // can focus the next tab after switching. Samer review PR #159 §2.
  const typeTabRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const [name, setName] = useState('');
  const [documentDate, setDocumentDate] = useState(() =>
    wireToDateInput(todayLocalIso()),
  );
  const [doctorName, setDoctorName] = useState('');
  const [facilityName, setFacilityName] = useState('');
  const [tags, setTags] = useState('');
  const [pickedFormId, setPickedFormId] = useState<string>('');
  const [files, setFiles] = useState<FileRow[]>([]);
  // Lab-row state keyed by stable per-row local id so React keys don't
  // collide. The wire payload strips the key before send.
  //
  // `isEditing` mirrors Flutter `PatientDocumentLab.isEdit` — a
  // transient UI flag (NOT shipped on the wire). When true the row
  // renders the full editor; when false it renders a read-only summary
  // card with Edit / Delete icons. Only used by locked-mode (Lab
  // Linkage). Unlocked mode keeps the always-editable inline pattern
  // and ignores this flag.
  const [labRows, setLabRows] = useState<
    Array<{ key: string; row: PatientDocumentLab; isEditing: boolean }>
  >([]);
  // Procedure / Radiology types pick a code from the master catalogue.
  // The picked value populates `data.title` + `data.code` on the body.
  const [pickedCode, setPickedCode] = useState<PatientDocumentData | null>(null);
  // PROD-899 edit-mode: files already attached to the row before the
  // edit started. Distinct from `files` (fresh uploads added during
  // this session). On Save we union both — existing files ship
  // verbatim (with optional `isDeleted: true` overlay when the user
  // removes them) and new uploads ship after their POST resolves.
  const [existingFiles, setExistingFiles] = useState<PatientDocumentFile[]>(
    [],
  );
  // PROD-903: per-file rename state. Tracks which file row is in edit
  // mode (null = none) and the in-progress draft name. Commit rebuilds
  // the underlying File with the new name.
  const [renamingLocalId, setRenamingLocalId] = useState<string | null>(null);
  const [renameDraft, setRenameDraft] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Stable id linking the drop-zone <label> to the hidden file <input>.
  // We open the picker via native label association (htmlFor) rather than
  // a programmatic `inputRef.click()` — the JS click is fragile inside a
  // nested base-ui Sheet focus-trap and under the local-dev SignalR
  // reconnect storm (it would silently no-op → "Browse not working").
  // A native label is a trusted gesture the browser always honours.
  const fileInputId = useId();
  const [dragActive, setDragActive] = useState(false);
  // Codex review (2026-07-17): the Save button previously re-enabled the
  // instant `save.mutateAsync` resolved, WHILE the caller's `onSaved`
  // follow-up (e.g. Lab Linkage's ReceivedLabResults call) was still in
  // flight — a user could double-click Save and race a second
  // upload+create against the first attempt's still-pending callback.
  // Track the callback separately from upload/save so it gates the
  // button too.
  const [onSavedPending, setOnSavedPending] = useState(false);

  const upload = useUploadDocument();
  const save = useSaveDocument();
  const orgForms = useOrganizationForms({ enabled: open && docType === DocumentType.Forms });

  // PROD-902: org-level max upload limit (MB). Mirrors
  // `add-file-observation.tsx:65-67` pattern.
  //
  // Samer PR #339 review item 1: a missing org config must be LOUD,
  // not silently permissive at the Flutter-default 10 MB. We surface
  // both an inline banner (visible in the popup) and a console.error
  // so ops triage can find the misconfigured tenant. The fallback
  // limit is preserved (Flutter parity — Flutter ships 10 when null)
  // BUT the missing-config state is no longer silent. Users get a
  // chance to abort + contact support before committing PHI.
  //
  // **Backend dependencies** (Samer review items 2 + 5):
  // - PROD-938: the .NET `UploadController` is the authoritative
  //   HIPAA gate. Client-side checks here are defense-in-depth —
  //   they reduce blast radius (orphan blob writes, bandwidth, UX)
  //   but cannot be the sole guard. Backend ticket tracks server-
  //   side enforcement audit (extension allowlist + size + org-id
  //   binding).
  // - PROD-939: `maxDocumentUploadLimit` org-scope correctness
  //   depends on the backend assembling the JWT `UserData` row from
  //   the user's active practice/org row, not a global default.
  //   Backend ticket tracks tenant-isolation audit.
  const authUser = useAuthStore((s) => s.user);
  const orgUploadLimit = authUser?.maxDocumentUploadLimit;
  const uploadLimitMissing =
    orgUploadLimit === null || orgUploadLimit === undefined;
  const maxUploadMb = orgUploadLimit ?? DEFAULT_DOCUMENT_MAX_UPLOAD_MB;

  // Warn once per popup-open when the org config is missing. Fires on
  // initial mount AND on every reopen so the warning isn't suppressed
  // by a stale-state silence.
  useEffect(() => {
    if (!open) return;
    if (!uploadLimitMissing) return;
    // eslint-disable-next-line no-console
    console.error(
      '[NewFilePopup] maxDocumentUploadLimit is missing on auth-store.user. ' +
        'Falling back to ' +
        DEFAULT_DOCUMENT_MAX_UPLOAD_MB +
        ' MB. Bad org config — see PROD-902 / PR 339 review.',
    );
  }, [open, uploadLimitMissing]);

  // PROD-939 runtime observability — log the (organizationId,
  // practiceId, maxDocumentUploadLimit) triple on popup open so ops
  // can correlate the limit against the active org in dev/Sentry. If
  // a wrong-org limit ever ships, the log makes it findable in the
  // session that triggered it. Logs on the (a) initial open AND
  // (b) every org/practice/limit change while open (e.g. JWT
  // refresh mid-session).
  //
  // **Log level = `console.debug`** (Samer review #3): Sentry's
  // default `Breadcrumbs` integration captures log/info/warn/error
  // breadcrumbs but excludes `debug` by default — and DevTools tiers
  // `debug` into a separate filter group. This keeps the
  // observability greppable when ops needs it (filter on
  // `[NewFilePopup] tenant-scope context` in DevTools or attach a
  // Sentry integration that opts into debug breadcrumbs) without
  // polluting prod Sentry dashboards. If a future Sentry config
  // enables debug breadcrumbs globally, add a per-prefix exclude on
  // `[NewFilePopup]` at the Sentry integration layer rather than
  // downgrading further here.
  useEffect(() => {
    if (!open) return;
    // eslint-disable-next-line no-console
    console.debug(
      '[NewFilePopup] tenant-scope context',
      JSON.stringify({
        organizationId: authUser?.organizationId ?? null,
        practiceId: authUser?.practiceId ?? null,
        maxDocumentUploadLimit: orgUploadLimit ?? null,
      }),
    );
  }, [
    open,
    authUser?.organizationId,
    authUser?.practiceId,
    orgUploadLimit,
  ]);

  // Open / close lifecycle.
  //
  // - **Close**: reset every field to the unmounted defaults so a
  //   re-open starts clean. `lockedType` (Lab Linkage) snaps back to
  //   its locked type; otherwise we land on Notes.
  // - **Open in EDIT mode** (PROD-899): hydrate every field from
  //   `initialDocument`. Type tab row is hidden in edit mode (see
  //   render block below) so the user can't switch type mid-edit.
  //
  // We deliberately exclude `save` / `upload` from the dep array per
  // the Storybook + React 19 + StrictMode rerun edge-case documented
  // below (Minified React error #185 — "Too many re-renders").
  // Including them would re-call `.reset()` on every render, burning
  // React's 50-iter render budget on the `Closed` story.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!open) {
      // Close path — reset to defaults.
      setDocType(lockedType ?? DocumentType.Notes);
      setName('');
      setDocumentDate(wireToDateInput(todayLocalIso()));
      setDoctorName('');
      setFacilityName('');
      setTags('');
      setPickedFormId('');
      setFiles([]);
      setLabRows([]);
      setPickedCode(null);
      setExistingFiles([]);
      // PROD-903: clear rename-in-progress state so a re-open starts
      // with no row in edit mode.
      setRenamingLocalId(null);
      setRenameDraft('');
      setOnSavedPending(false);
      upload.reset();
      save.reset();
      return;
    }
    // Open path. EDIT mode hydrates from `initialDocument`; ADD mode
    // is left in its default state (the close path already set it).
    if (initialDocument) {
      setDocType(
        (initialDocument.type as DocumentTypeCode | undefined) ??
          DocumentType.Notes,
      );
      setName(initialDocument.name ?? '');
      setDocumentDate(wireToDateInput(initialDocument.documentDate));
      setDoctorName(initialDocument.doctorName ?? '');
      setFacilityName(initialDocument.facilityName ?? '');
      setTags(initialDocument.tags ?? '');
      setPickedFormId('');
      setFiles([]);
      setExistingFiles(
        (initialDocument.patientDocumentFiles ?? []).filter(
          (f): f is PatientDocumentFile => !!f && !f.isDeleted,
        ),
      );
      // Code-driven types hydrate from `data.{title, code}`.
      if (
        initialDocument.type === DocumentType.Procedure ||
        initialDocument.type === DocumentType.Radiology
      ) {
        setPickedCode({
          title: initialDocument.data?.title ?? null,
          code: initialDocument.data?.code ?? null,
        });
      } else {
        setPickedCode(null);
      }
      // Lab rows ship in their non-editing summary card by default
      // (matches Flutter — user can click pencil to enter the editor
      // per row). Empty-tombstone rows are excluded so we don't
      // resurrect previously-deleted rows.
      setLabRows(
        (initialDocument.patientDocumentLabs ?? [])
          .filter((r): r is PatientDocumentLab => !!r && !r.isDeleted)
          .map((row) => ({
            key: nextLabRowKey(),
            row,
            isEditing: false,
          })),
      );
      upload.reset();
      save.reset();
    }
  }, [open, initialDocument]);

  // Lab rows use the Order-Requests (locked) flow on EVERY surface
  // (Andrew 2026-06-10): start with zero rows + an "Add Another Lab"
  // affordance — no auto-seeded row. (Previously the unlocked Documents
  // tab seeded one empty row; removed for parity with Order Requests.)

  // Locked-Lab default name — "Lab results YYYY-MM-DD" so the user
  // doesn't have to type one. Only seeds on open + only if the user
  // hasn't already typed.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!open) return;
    if (lockedType !== DocumentType.Labs) return;
    if (name.trim().length > 0) return;
    setName(`Lab results ${documentDate}`);
  }, [open, lockedType]);

  // Clear the picked CPT / radiology code when switching AWAY from
  // a code-driven type. Avoids the body shipping stale `data` on a
  // subsequent Note / Form save.
  useEffect(() => {
    if (!CODE_DRIVEN_TYPES.includes(docType)) {
      setPickedCode(null);
    }
  }, [docType]);

  const isCodeDrivenType = CODE_DRIVEN_TYPES.includes(docType);

  // Switch the active document type AND clear every per-type selection /
  // entry (audit 2026-06-09). Without this, a value from one type bleeds
  // into the next — e.g. a Lab auto-name showing on the Note tab, or a
  // picked Procedure code persisting after switching to Radiology (both are
  // code-driven, so the "clear on leaving code-driven" effect doesn't fire).
  // Type tabs are hidden in edit mode, so this never wipes hydrated values.
  const handleTypeChange = (next: DocumentTypeCode) => {
    if (next === docType) return;
    setName('');
    setDoctorName('');
    setFacilityName('');
    setTags('');
    setPickedCode(null);
    setPickedFormId('');
    setLabRows([]);
    setDocType(next);
  };

  // PROD-904: Lab name auto-default. Flutter `lab_document_widget.dart:50-52`
  // sets `name = 'Lab results ${scDateFormat(DateTime.now())}'` on init.
  // We mirror that for the unlocked Documents tab — only when the user
  // hasn't typed anything yet. The existing locked-mode (Lab Linkage)
  // default at the open-effect below stays put.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!open) return;
    if (lockedType) return; // locked-mode default handled in open-effect
    if (docType !== DocumentType.Labs) return;
    if (name.trim().length > 0) return;
    setName(`Lab results ${formatScDate(new Date())}`);
  }, [open, docType, lockedType]);

  // For Form type, pre-fill `name` from the picked template so the
  // user doesn't have to retype it.
  useEffect(() => {
    if (docType !== DocumentType.Forms) return;
    const picked = (orgForms.data ?? []).find((f) => f.id === pickedFormId);
    // Flutter binds the picked form's `formName` to the document `name`
    // (`forms_document_widget.dart`). Prefer `formName`, fall back to `name`.
    const formLabel = (picked?.formName as string | undefined) ?? picked?.name;
    if (formLabel) setName(formLabel);
  }, [docType, orgForms.data, pickedFormId]);

  const pickedTemplate = useMemo(
    () => (orgForms.data ?? []).find((f) => f.id === pickedFormId),
    [orgForms.data, pickedFormId],
  );

  // Per-row validity — Samer review PR #139 §3. Previously the filter
  // ("not fully empty") and the validation ("complete") were two
  // different thresholds, so a row with only a result filled would
  // pass the filter and silently ship a half-row to the backend.
  // Now they're aligned: a row is either DELETED (kept for tombstone)
  // OR COMPLETE (lab + result + unit, ships) OR INCOMPLETE (blocks
  // submit). No row is silently dropped.
  const rowValidity = useMemo(() => {
    return labRows.map((entry) => {
      const r = entry.row;
      if (r.isDeleted) return { key: entry.key, state: 'deleted' as const };
      const hasLab = !!r.labName?.code;
      const hasResult = !!String(r.result ?? '').trim();
      const hasUnit = !!r.unitType?.code;
      const isComplete = hasLab && hasResult && hasUnit;
      const isEmpty = !hasLab && !hasResult && !hasUnit;
      if (isComplete) return { key: entry.key, state: 'complete' as const };
      if (isEmpty) return { key: entry.key, state: 'empty' as const };
      return {
        key: entry.key,
        state: 'incomplete' as const,
        missing: {
          lab: !hasLab,
          result: !hasResult,
          unit: !hasUnit,
        },
      };
    });
  }, [labRows]);

  const incompleteRows = useMemo(
    () => rowValidity.filter((v) => v.state === 'incomplete'),
    [rowValidity],
  );

  // Submit rows: deleted (tombstone) + complete only. Empty + incomplete
  // are silently dropped only when they're empty (no user input); the
  // incomplete case is BLOCKED by canSubmit so we never reach the
  // mutation with a partial row.
  const submitLabRows = useMemo(
    () =>
      labRows
        .map((r, i) => ({ row: r.row, state: rowValidity[i]?.state }))
        .filter(({ state }) => state === 'deleted' || state === 'complete')
        .map((entry) => entry.row),
    [labRows, rowValidity],
  );

  // Lab rows use the Order-Requests (locked) rule on EVERY surface
  // (Andrew 2026-06-10): structured rows are OPTIONAL — the user is
  // uploading a result file; entering rows is a bonus, not a gate. A row
  // left in edit mode (or half-filled / incomplete) blocks Save (Flutter
  // sets `enableSaveButton: false` while a row is being added / edited;
  // clicking Done flips it back). No "≥1 complete row" requirement.
  const hasValidLabs = useMemo(() => {
    if (docType !== DocumentType.Labs) return true;
    const someEditing = labRows.some((r) => r.isEditing);
    return incompleteRows.length === 0 && !someEditing;
  }, [docType, incompleteRows.length, labRows]);

  // Validation rules by document type (Flutter `document_bloc.dart`:
  //   Notes / Forms / Admin Referrals: name + date required, files
  //     required (V1 enforces; Flutter's bloc validation allows
  //     fileless but our popup is upload-driven)
  //   Labs: name + date required, files required, ≥1 complete lab row
  //   Procedure / Radiology: data.code (CPT) + date required, files
  //     OPTIONAL (Flutter's procedure widget doesn't require files —
  //     a procedure record without attached imaging is valid)
  // The `name` field for Procedure / Radiology is auto-set from the
  // picked code's title at submit time; no free-text entry required.
  const nameRequirementMet = isCodeDrivenType
    ? !!pickedCode?.code
    : !!name.trim();
  // PROD-899: in edit mode, existing-files satisfy the "files required"
  // gate alongside any new uploads. A user editing metadata-only on a
  // doc that already has attachments shouldn't have to re-upload.
  const hasAnyVisibleFile =
    files.length > 0 || existingFiles.some((f) => !f.isDeleted);
  const filesRequirementMet = isCodeDrivenType ? true : hasAnyVisibleFile;

  const canSubmit =
    nameRequirementMet &&
    !!documentDate &&
    filesRequirementMet &&
    !upload.isPending &&
    !save.isPending &&
    !onSavedPending &&
    (docType !== DocumentType.Forms || !!pickedFormId) &&
    hasValidLabs;

  const addFiles = (incoming: FileList | File[]) => {
    // PROD-901 + PROD-902: enforce file-type allowlist + maxDocumentUploadLimit
    // BEFORE we add the file to upload state. Mirrors Flutter
    // `pick_document_widget.dart:131-180` (drop zone) and :210-272 (Browse).
    //
    // Samer PR #339 review item 3 (extension-spoofing guard): in addition
    // to the suffix check, the browser-reported MIME (`File.type`) is
    // validated against an extension-keyed allowlist. Files with an
    // empty MIME pass (browser couldn't sniff — Flutter parity). Files
    // with a populated MIME that disagrees with the extension are
    // rejected as part of the `badExtNames` category (same toast — the
    // user sees the same UX whether the bad input is a `.exe` or a
    // `.exe` masquerading as `.pdf`).
    //
    // Aggregates rejections so the user sees ONE toast per category
    // even when several files fail. Samer review item 4: valid files
    // in a mixed batch ARE added (see test
    // `accepts a mixed batch — keeps valid files…` for explicit cov.)
    const accepted: FileRow[] = [];
    const badExtNames: string[] = [];
    const oversizeNames: string[] = [];

    for (const f of Array.from(incoming)) {
      if (!isValidExtension(f.name) || !isMimeConsistent(f)) {
        badExtNames.push(f.name);
        continue;
      }
      if (isFileSizeGreater(f.size, maxUploadMb)) {
        oversizeNames.push(f.name);
        continue;
      }
      accepted.push({
        localId: `${f.name}-${f.size}-${f.lastModified}-${Math.random()
          .toString(36)
          .slice(2, 8)}`,
        file: f,
      });
    }

    if (badExtNames.length > 0) {
      toast.error(TOAST_DOCUMENT_FILE_INVALID);
    }
    if (oversizeNames.length > 0) {
      toast.error(oversizeMessage(maxUploadMb));
    }
    if (accepted.length > 0) {
      // Single-file on EVERY surface (Andrew 2026-06-10): one document =
      // one attached file. A fresh pick REPLACES the current selection
      // (and a multi-file drag keeps only the first accepted) rather than
      // appending. The drop zone is hidden once a file is present (see
      // `fileDropZone`), so the only way to change it is delete-then-add.
      setFiles([accepted[0]]);
    }
  };

  const removeFile = (localId: string) =>
    setFiles((prev) => prev.filter((f) => f.localId !== localId));

  // PROD-903 per-file rename helpers. Mirrors Flutter
  // `pick_document_widget.dart` `documentEditView` (lines 374-467):
  //   - Pencil enters edit mode for that row
  //   - Input value strips the extension; maxLength = 100 - extLen
  //   - Check commits: empty trim → toast "Please enter file name";
  //     else rebuild the File with `new File([blob], trimmed+ext)`
  //   - X cancels (state unchanged)
  const startRenameRow = (row: FileRow) => {
    setRenamingLocalId(row.localId);
    setRenameDraft(stripExtension(row.file.name));
  };

  const cancelRenameRow = () => {
    setRenamingLocalId(null);
    setRenameDraft('');
  };

  const commitRenameRow = (row: FileRow) => {
    const trimmed = renameDraft.trim();
    if (trimmed.length === 0) {
      // Verbatim Flutter `pick_document_widget.dart:444` toast.
      toast.error('Please enter file name');
      return;
    }
    const ext = extensionFor(row.file.name);
    const nextName = `${trimmed}${ext}`;
    if (nextName === row.file.name) {
      // No-op rename — just exit edit mode.
      cancelRenameRow();
      return;
    }
    const renamedFile = new File([row.file], nextName, {
      type: row.file.type,
      lastModified: row.file.lastModified,
    });
    setFiles((prev) =>
      prev.map((f) => (f.localId === row.localId ? { ...f, file: renamedFile } : f)),
    );
    cancelRenameRow();
  };

  const onSubmit = async () => {
    if (!canSubmit) return;
    try {
      // Step 1 — upload all files in parallel via Promise.allSettled
      // so we get a complete picture of which succeeded vs failed
      // before deciding whether to AddUpdate (Samer review PR #137 §2).
      //
      // Partial-failure semantics: if ANY upload fails, we bail BEFORE
      // calling AddUpdate. The successful uploads leave orphan blobs
      // in Azure storage — there is no client-side cleanup path here.
      // Reclassified 2026-05-21 (7th cross-cutting #8 instance): Flutter
      // `DeleteDocumentUseCase` is dead code — registered in DI at
      // `dependency_injection.dart:2053` but `DeleteDocumentEvent` doesn't
      // exist (no class, no dispatch site, no handler — grep-verified).
      // Both Flutter and React equally lack DeleteImage dispatch from
      // documents → MATCH-IDIOMATIC per "do as Flutter exactly". The
      // orphan-blob loophole is a HIPAA minimization concern flagged
      // for a separate cross-platform ticket (Andrew triage). Until
      // that ticket lands, we log the orphan URLs so ops can reconcile
      // against Azure storage metrics manually.
      //
      // Why allSettled over Promise.all: the latter rejects on the
      // first failure but still races the other uploads to completion
      // in the background, producing even more orphans. allSettled
      // gives us a clean accounting of every attempt.
      const results = await Promise.allSettled(
        files.map((row) => upload.mutateAsync({ file: row.file })),
      );

      const orphans: string[] = [];
      const uploaded: PatientDocumentFile[] = [];
      const failures: { name: string; reason: unknown }[] = [];
      results.forEach((result, idx) => {
        const row = files[idx];
        if (result.status === 'fulfilled') {
          uploaded.push({
            id: null,
            patientDocumentId: null,
            url: result.value.url,
            fileName: row.file.name,
            fileType: extensionFor(row.file.name),
            isDeleted: false,
          });
        } else {
          failures.push({ name: row.file.name, reason: result.reason });
        }
      });

      if (failures.length > 0) {
        // Collect successful URLs as orphans for ops triage. DeleteImage
        // from documents is dead code on Flutter (see partial-failure
        // header comment above for the cross-cutting #8 reclassification)
        // so client-side cleanup is out of scope until Andrew opens the
        // HIPAA-orphan-blob ticket that covers Flutter AND React equally.
        // Until that lands, these blobs persist until manual cleanup.
        uploaded.forEach((u) => u.url && orphans.push(u.url));
        // eslint-disable-next-line no-console
        console.error('[NewFilePopup] partial upload failure', {
          failed: failures.map((f) => f.name),
          orphanBlobs: orphans,
        });
        toast.error(
          `Couldn't upload ${failures.length} of ${files.length} file${files.length === 1 ? '' : 's'}. Try again.`,
        );
        return;
      }

      // Step 2 — AddUpdate the document with the uploaded URLs.
      //
      // Code-driven types (Procedure / Radiology) get their `name` AND
      // `data.{title, code}` from the picked master row. Flutter does
      // the same: the procedure widget builds `Data(title: ..., code:
      // ...)` from the picked CodeList and the body's `name` mirrors
      // the title for display in the list. Non-code-driven types ship
      // `name` from the free-text input and `data: { title: null,
      // code: null }` (wire-canonical per the dev curl).
      const wireName = isCodeDrivenType
        ? pickedCode?.title ?? pickedCode?.code ?? ''
        : name.trim();
      const wireData: PatientDocumentData =
        isCodeDrivenType && pickedCode
          ? { title: pickedCode.title ?? null, code: pickedCode.code ?? null }
          : { title: null, code: null };

      // PROD-899: in edit mode, spread the existing document FIRST so
      // server-set audit fields (createdById, createdOn, reviewedById,
      // reviewedByName, reviewedOn, updatedById, updatedByName, updatedOn,
      // and the row `id`) ride along verbatim. Then overlay the
      // user-edited fields on top.
      //
      // Existing files: ship the existingFiles state as-is. The user
      // can mark items deleted via the `existingFiles` row UI, which
      // sets `isDeleted: true` on the entry. The backend treats
      // patientDocumentFiles as the source of truth on save and
      // tombstones the marked entries.
      //
      // New uploads merge in after the existing list.
      const mergedFiles: PatientDocumentFile[] = [
        ...existingFiles,
        ...uploaded,
      ];

      const body: PatientDocumentModel = initialDocument
        ? {
            ...initialDocument,
            // Overlay user-edited fields. Type stays put per design
            // (tab row hidden in edit mode), so re-take from existing.
            name: wireName,
            type: initialDocument.type ?? docType,
            documentDate: dateInputToWire(documentDate),
            doctorName: doctorName.trim() || null,
            facilityName: facilityName.trim() || null,
            tags: tags.trim() || null,
            data: wireData,
            // `isDeleted` is the archive flag (PROD-900) — keep
            // whatever the row had. Edit isn't archive.
            isDeleted: initialDocument.isDeleted ?? false,
            patientDocumentFiles: mergedFiles,
            patientDocumentLabs:
              docType === DocumentType.Labs ? submitLabRows : [],
          }
        : {
            id: null,
            patientId,
            // Explicit `?? null` (Samer review PR #137 §4): the prop is
            // `string | null | undefined`. JSON.stringify drops `undefined`
            // values entirely, which the .NET model binder may reject as
            // a missing field. Coercing to `null` matches the dev-curl
            // wire-shape contract (`"encounterId": null` ships present).
            encounterId: encounterId ?? null,
            name: wireName,
            type: docType,
            documentDate: dateInputToWire(documentDate),
            doctorName: doctorName.trim() || null,
            facilityName: facilityName.trim() || null,
            tags: tags.trim() || null,
            reviewedById: null,
            reviewedByName: null,
            reviewedOn: null,
            data: wireData,
            createdById: null,
            createdByName: null,
            createdOn: null,
            updatedById: null,
            updatedByName: null,
            updatedOn: null,
            isDeleted: false,
            patientDocumentFiles: mergedFiles,
            // Lab rows ship only for the Labs type; other types send the
            // empty array per the dev-curl wire contract.
            patientDocumentLabs:
              docType === DocumentType.Labs ? submitLabRows : [],
          };

      // useSaveDocument returns the new document id as a bare string
      // (curl-4 wire shape). Pass it straight through to onSaved.
      const documentId = await save.mutateAsync(body);
      // Optional consumer chain (Lab Linkage Order Requests tab uses
      // this to call `ReceivedLabResults` with the new document id).
      // If `onSaved` throws, we stay open — same retry-friendly
      // semantics as the upload-failure path above. `onSavedPending`
      // keeps Save disabled for the DURATION of this callback (Codex
      // review 2026-07-17) — otherwise the button re-enables the
      // instant `save.mutateAsync` above resolves, and a double-click
      // during the still-in-flight callback would fire a second
      // upload+create racing the first attempt's follow-up.
      if (onSaved) {
        setOnSavedPending(true);
        try {
          await onSaved(documentId);
        } finally {
          setOnSavedPending(false);
        }
      }
      toast.success(isEditMode ? 'Document updated' : 'Document added');
      onOpenChange(false);
    } catch {
      // Global mutation-cache toast surfaces the failure (per the
      // project's toast convention). The popup stays open so the
      // user can retry or correct the form.
    }
  };

  // ─── Drag / drop wiring ────────────────────────────────────────────
  const onDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(true);
  };
  const onDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(false);
  };
  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(false);
    if (e.dataTransfer.files?.length) addFiles(e.dataTransfer.files);
  };

  const isBusy = upload.isPending || save.isPending || onSavedPending;

  // File drop zone — extracted into a local JSX const so we can render
  // it in different positions depending on mode. Flutter Lab Linkage
  // places it RIGHT AFTER the FILE TYPE block (above the form fields);
  // the unlocked Documents tab keeps the original layout (drop zone
  // last, after the metadata fields).
  const fileDropZone = (
    <div className="space-y-2">
      {/* The design's field label (PaDocumentUpload `.label`): label type, meta ink, sentence case. */}
      <label data-slot="drop-zone-label">
        File
        {!isCodeDrivenType && (
          <span className="text-status-error"> *</span>
        )}
      </label>
      {/* Single-file (Andrew 2026-06-10): the drop zone shows ONLY while
          no file is attached. Once a file is picked it collapses to the
          file row below (rename + delete) — delete brings the drop zone
          back. Matches the screenshot + the Order-Requests (locked) flow. */}
      {!hasAnyVisibleFile ? (
      // Native <label> with the <input> NESTED INSIDE it (implicit association) —
      // the WHOLE drop zone opens the OS file picker on click (browser-handled
      // trusted gesture). No JS `.click()`, so it can't be swallowed by the nested
      // Sheet focus-trap or stalled by the dev SignalR storm.
      //
      // Do NOT also add `htmlFor` here. Nesting AND htmlFor both associate the same
      // input, so one physical click activated it TWICE: the label forwards a click
      // to the input, that click bubbles back up through the label (the input is a
      // descendant), and the label forwards again. Measured 2 input activations /
      // 4 label click events per click. Chromium under Playwright tolerated it and
      // showed one picker, but real browsers open-then-immediately-cancel, so Browse
      // did nothing while drag-and-drop — which never goes through the label — kept
      // working. `consent-external-upload-dialog.tsx` already uses nesting alone and
      // was unaffected; this now matches it.
      <DropZone
        as="label"
        look="docs"
        dragging={dragActive}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
        title="Drop File Here or Browse"
        hint={`PDF, RTF, JPG, JPEG, PNG, CSV, XLSX & DOCX file formats are supported. Max ${maxUploadMb} MB.`}
        // Visual-only Browse in the design's secondary button look: the surrounding <label> is the real click target,
        // so this isn't a separate button (a nested button/label would double-fire or be invalid markup).
        action={
          <span data-slot="drop-zone-browse" aria-hidden>
            Browse
          </span>
        }
      >
        <input
          ref={fileInputRef}
          id={fileInputId}
          type="file"
          // PROD-901: OS-level pre-filter so the user only sees allowed
          // file types in their picker. The `addFiles` validation still
          // runs as a defense-in-depth check for drag-and-drop and for
          // browsers that ignore the attribute.
          accept={DOCUMENT_ACCEPT_ATTR}
          // Single-file on every surface (Andrew 2026-06-10): one document
          // = one attached file.
          multiple={false}
          // `sr-only` (not `hidden`): the input stays focusable so
          // keyboard users can open the picker with Space/Enter; the
          // label handles pointer activation.
          className="sr-only"
          onChange={(e) => {
            if (e.target.files?.length) addFiles(e.target.files);
            // Reset the input so re-picking the same file fires
            // the change event again.
            e.target.value = '';
          }}
        />
      </DropZone>
      ) : null}

      {/* PROD-899: existing files (edit mode). Each row shows a
          clickable file-name link + a remove (X) icon. Removing
          marks the entry with `isDeleted: true` so the next save
          tombstones it server-side — same model as Flutter
          `pick_document_widget.dart` documentView (lines 310-372).
          The Azure blob itself is NOT deleted (HIPAA orphan-blob
          concern stays open per the 2026-05-21 cross-platform
          ticket). */}
      {existingFiles.some((f) => !f.isDeleted) && (
        <ul className="mt-2 space-y-1">
          {existingFiles.map((file, idx) => {
            if (file.isDeleted) return null;
            const key = file.id ?? `existing-${idx}-${file.fileName ?? ''}`;
            return (
              <li
                key={key}
                className="flex items-center gap-2 rounded-md border border-border-default bg-surface-card px-2 py-1.5 text-sm"
              >
                <FileText
                  className="size-4 text-text-secondary shrink-0"
                  aria-hidden
                />
                {file.url ? (
                  <a
                    href={file.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 truncate text-brand-primary hover:underline"
                  >
                    {file.fileName ?? '—'}
                  </a>
                ) : (
                  <span className="flex-1 truncate">
                    {file.fileName ?? '—'}
                  </span>
                )}
                <button
                  type="button"
                  onClick={() =>
                    setExistingFiles((prev) =>
                      prev.map((f, i) =>
                        i === idx ? { ...f, isDeleted: true } : f,
                      ),
                    )
                  }
                  className="icon-action"
                  aria-label={`Remove ${file.fileName ?? 'file'}`}
                >
                  <X className="size-4" aria-hidden />
                </button>
              </li>
            );
          })}
        </ul>
      )}

      {/* Selected files list (new uploads added this session).
          PROD-903: pencil per row enters inline edit mode; check
          commits, X cancels. Renamed value is stored on the underlying
          File via `new File([blob], newName, {…})`, and the multipart
          upload's filename header ships the new value. */}
      {files.length > 0 && (
        <ul className="mt-2 space-y-1">
          {files.map((row) => {
            const isRenaming = renamingLocalId === row.localId;
            const ext = extensionFor(row.file.name);
            return (
              <li
                key={row.localId}
                className="flex items-center gap-2 rounded-md border border-border-default bg-surface-card px-2 py-1.5 text-sm"
              >
                <FileText
                  className="size-4 text-text-secondary shrink-0"
                  aria-hidden
                />
                {isRenaming ? (
                  <>
                    <input
                      type="text"
                      autoFocus
                      value={renameDraft}
                      onChange={(e) => setRenameDraft(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          commitRenameRow(row);
                        } else if (e.key === 'Escape') {
                          e.preventDefault();
                          cancelRenameRow();
                        }
                      }}
                      // PROD-903: Flutter caps the EDIT input at
                      // `100 - extension.length` so the final
                      // `name+ext` stays under 100 chars total
                      // (`pick_document_widget.dart:404`).
                      maxLength={Math.max(1, 100 - ext.length)}
                      aria-label={`Rename ${row.file.name}`}
                      className="flex-1 h-8 px-2 rounded border border-border-default bg-grey-100 text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary"
                    />
                    <span className="text-text-hint">{ext}</span>
                    <button
                      type="button"
                      onClick={() => commitRenameRow(row)}
                      className="icon-action"
                      aria-label="Confirm rename"
                    >
                      <Check className="size-4" aria-hidden />
                    </button>
                    <button
                      type="button"
                      onClick={cancelRenameRow}
                      className="icon-action"
                      aria-label="Cancel rename"
                    >
                      <X className="size-4" aria-hidden />
                    </button>
                  </>
                ) : (
                  <>
                    <span className="flex-1 truncate">{row.file.name}</span>
                    <span className="text-text-hint">
                      {(row.file.size / 1024).toFixed(1)} KB
                    </span>
                    <button
                      type="button"
                      onClick={() => startRenameRow(row)}
                      className="icon-action"
                      aria-label={`Rename ${row.file.name}`}
                    >
                      <Pencil className="size-4" aria-hidden />
                    </button>
                    <button
                      type="button"
                      onClick={() => removeFile(row.localId)}
                      className="icon-action"
                      aria-label={`Remove ${row.file.name}`}
                    >
                      <X className="size-4" aria-hidden />
                    </button>
                  </>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );

  const panelTitle = isEditMode ? 'Edit File' : 'New File';

  return (
    <Drawer
      open={open}
      onClose={() => onOpenChange(false)}
      side="right"
      // Width was expressed via the `data-[side=right]:w-[...px]
      // data-[side=right]:!max-w-none` Tailwind idiom on the Sheet primitive
      // (see the retired comment this replaces, and the Sheet-era 384px clamp
      // regression it guarded against). Drawer takes width as a plain prop —
      // an inline style, not a class — so that idiom no longer applies; the
      // numeric values themselves (500 / 640, Flutter `encounter_docs_screen.dart:278`)
      // are unchanged.
      width={lockedType ? 640 : 520}
      showCloseButton={false}
      // Scrim: Flutter's `SlidingPopup` barrier is `Colors.black26`
      // (= 0x42000000, alpha 66/255 = 25.9%) with NO blur, the app-wide
      // `barrierColor` idiom (e.g. `alert_dialog.dart:24`,
      // `popup_utils.dart:576`). The Sheet default is a lighter blurred
      // scrim, so both are overridden here rather than in the primitive —
      // the other 5 Sheet consumers (still on Sheet) keep their default.
      // Uses `--color-surface-overlay` (#0F0F0F), the DS's own overlay base
      // that `Modal` and `Drawer` already dim with, instead of a raw black,
      // so the value stays on-token. OWED: the DS has no scrim-alpha token,
      // so 26% is expressed inline.
      overlayClassName="bg-surface-overlay/26 supports-backdrop-filter:backdrop-blur-none"
      className="flex flex-col gap-0 p-0"
      // Header — Cancel (left) / centred title / Save (right). Same on every
      // surface (Andrew 2026-06-09): patient chart Docs + appointment Docs
      // (unlocked) now use the same top-bar header as Order Requests, instead
      // of a left title + bottom Save/Cancel.
      // Title is passed as a styled span (Drawer's own `panel-title` role
      // class doesn't carry the 17px/bold/brand-depth spec) rather than a
      // plain string, so the a11y name text ("Edit File"/"New File") stays
      // identical while the visible style is preserved. `ariaLabel` is set
      // to the same text to silence Drawer's non-plain-text-title dev
      // warning; `Dialog.Title`'s `aria-labelledby` still wins the actual
      // accessible name either way.
      title={
        <span className="text-size-title font-bold text-brand-depth">
          {panelTitle}
        </span>
      }
      ariaLabel={panelTitle}
      // ⚠️ Drawer's `description` renders VISIBLY (text-sm, no sr-only
      // variant) — unlike the retired Sheet's `SheetDescription`, which this
      // call site set `sr-only` deliberately ("no visible subtitle — matches
      // the locked design"). Drawer has no prop to keep it hidden, so rather
      // than either violating that locked design or hacking a one-off
      // sr-only override onto DS-owned markup, the description is DROPPED
      // here. This loses the `aria-describedby` hookup the old
      // SheetDescription gave assistive tech; UNVERIFIED whether that is
      // material since the dialog's accessible NAME (the primary a11y
      // requirement) is unaffected. Flagging for Ahmed's visual-traverse
      // list alongside the headerLeading centring note below.
      headerLeading={
        <button
          type="button"
          onClick={() => onOpenChange(false)}
          disabled={isBusy}
          className="text-sm text-brand-primary disabled:opacity-50"
        >
          Cancel
        </button>
      }
      headerActions={
        <button
          type="button"
          onClick={onSubmit}
          disabled={!canSubmit}
          className={cn(
            'text-sm font-semibold transition-colors',
            canSubmit
              ? 'text-brand-primary'
              : 'text-text-muted opacity-60 cursor-not-allowed',
          )}
        >
          {isBusy ? 'Saving…' : 'Save'}
        </button>
      }
    >
        <div className="flex-1 overflow-y-auto scrollbar-hide pl-5.75 pr-6 pt-6 pb-3 space-y-6.5">
          {/* Samer PR #339 review item 1 — visible warning when the
              org's `maxDocumentUploadLimit` is missing on auth-store.
              Surfaces the bad config to the user BEFORE they commit
              PHI rather than silently falling back to a permissive
              default. The fallback is preserved (Flutter parity) but
              the misconfig is no longer silent. */}
          {uploadLimitMissing ? (
            <div
              role="alert"
              className="rounded-md border border-status-warning/40 bg-status-warning-bg/60 px-3 py-2 text-sm text-status-amber-text"
            >
              {TOAST_UPLOAD_LIMIT_MISSING}
            </div>
          ) : null}

          {/* Locked-mode FILE TYPE block — single big icon + label,
              centered above the form fields. No tab row. */}
          {lockedType ? (
            <div className="space-y-2">
              <p className="text-xs font-bold uppercase tracking-wide text-text-hint">
                File Type
              </p>
              {(() => {
                const lockedOpt = TYPE_OPTIONS_V1.find(
                  (o) => o.value === lockedType,
                );
                if (!lockedOpt) return null;
                const Glyph = lockedOpt.icon;
                return (
                  <div className="flex flex-col items-start gap-1">
                    <Glyph className="size-6 text-text-secondary" aria-hidden />
                    <span className="text-sm font-semibold text-brand-primary">
                      {lockedOpt.label}
                    </span>
                  </div>
                );
              })()}
            </div>
          ) : null}
          {/* Locked-mode (Lab Linkage) places the file upload tool
              IMMEDIATELY after FILE TYPE, before the form fields —
              matches Flutter Lab upload screen ordering. */}
          {lockedType ? fileDropZone : null}
          {/* Type picker — horizontal icon-tabs row matching the Flutter
              widget (Note · Form · Lab · Radiology · Procedure · Admin &
              Referrals). The active tab takes brand-primary on icon +
              label; inactive tabs are muted. Labels can wrap onto a
              second line ("Admin & Referrals") without disrupting the
              row height.
              Hidden when `lockedType` is set (Lab Linkage Order
              Requests tab — user is uploading a result, not picking a
              category). Also hidden in EDIT mode (PROD-899) — the
              existing row's type stays put; switching mid-edit would
              break code-driven forms and diverge from Flutter. */}
          {lockedType || isEditMode ? null : (
          <div className="space-y-2">
          {/* "FILE TYPE" eyebrow. Flutter draws it above the row:
              `new_file_popup_widget.dart:216-217`
              `boldLabelTextStyle(12, labelTextStyleTextColor)` over
              `"file type".toUpperCase()`. The legacy colour #AAAFC0 is a
              FROZEN mapping in FREEZE-MANIFEST.json (`#AAAFC0 ->
              --color-text-hint`), so this is the sanctioned token, not a
              new colour decision. Matches the `FILE` label's own idiom
              below. */}
          <label className="block text-xs font-bold uppercase tracking-wide text-text-hint">
            File type
          </label>
          <div
            role="tablist"
            aria-label="File type"
            className="flex min-h-16 items-start justify-between"
          >
            {TYPE_OPTIONS_V1.map((opt, idx) => {
              const Glyph = opt.icon;
              const selected = docType === opt.value;
              const onKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
                // WAI-ARIA tab pattern roving — arrow keys move focus +
                // selection, Home/End jump to ends. Only the active tab
                // is reachable via Tab; the rest are tabindex=-1 below
                // and surfaced via these handlers. Samer review PR #159 §2.
                let nextIdx: number | null = null;
                switch (e.key) {
                  case 'ArrowRight':
                    nextIdx = (idx + 1) % TYPE_OPTIONS_V1.length;
                    break;
                  case 'ArrowLeft':
                    nextIdx =
                      (idx - 1 + TYPE_OPTIONS_V1.length) %
                      TYPE_OPTIONS_V1.length;
                    break;
                  case 'Home':
                    nextIdx = 0;
                    break;
                  case 'End':
                    nextIdx = TYPE_OPTIONS_V1.length - 1;
                    break;
                  default:
                    return;
                }
                e.preventDefault();
                handleTypeChange(TYPE_OPTIONS_V1[nextIdx].value);
                typeTabRefs.current[nextIdx]?.focus();
              };
              return (
                <button
                  key={opt.value}
                  ref={(el) => {
                    typeTabRefs.current[idx] = el;
                  }}
                  type="button"
                  role="tab"
                  aria-selected={selected}
                  // Roving tabindex: only the active tab is in the Tab
                  // order; inactive tabs are reachable via arrow keys
                  // from the active one.
                  tabIndex={selected ? 0 : -1}
                  onClick={() => handleTypeChange(opt.value)}
                  onKeyDown={onKeyDown}
                  className={cn(
                    // `shrink-0` is load-bearing: without it flex shrinks the cell
                    // below 75 to fit the row (browser-measured 73px).
                    'flex w-18.75 shrink-0 min-h-13 flex-col items-center gap-1 rounded px-1 py-1.5 transition-colors',
                    'focus:outline-none focus:ring-2 focus:ring-brand-primary',
                    selected
                      ? 'text-brand-primary'
                      : 'text-text-hint hover:text-text-primary',
                  )}
                >
                  <Glyph className="size-5" aria-hidden />
                  <span
                    className={cn(
                      'text-center text-xs font-normal leading-tight',
                    )}
                  >
                    {opt.label}
                  </span>
                </button>
              );
            })}
          </div>
          </div>
          )}

          {/* File upload — directly under the type tabs (Flutter order:
              FILE TYPE → FILE → type-specific fields). Unlocked path; the
              locked path already places it under the single FILE TYPE. */}
          {lockedType ? null : fileDropZone}

          {/* Form-template picker (only when type=Form).
              PROD-1051 doc-forms (Andrew 2026-06-17): when the caller supports in-app
              fill-and-sign, the "Form template" dropdown lists the document-mode CONSENT
              templates and routes to the fill-and-sign popup instead. Callers that don't
              support it fall back to the legacy org-form-template download dropdown below.
              The consent query lives in the child so it only fires when the parent opted in
              AND the Form type is active. */}
          {docType === DocumentType.Forms && onPickConsentTemplate ? (
            <InAppConsentFormPicker
              onPick={onPickConsentTemplate}
              onUploadExternal={onUploadSignedConsent}
            />
          ) : docType === DocumentType.Forms ? (
            <div className="space-y-2">
              <label className="text-xs font-bold uppercase tracking-wide text-text-hint">
                Form template
              </label>
              {orgForms.isLoading ? (
                <BrandLoading size={48} label="Loading templates…" />
              ) : orgForms.isError ? (
                <p className="text-sm text-status-error">
                  Couldn't load form templates.
                </p>
              ) : (orgForms.data ?? []).length === 0 ? (
                <p className="text-sm text-text-muted">
                  No form templates configured for this organization.
                </p>
              ) : (
                // DS Select (PROD-1119): the compound shadcn tree
                // collapses to one prop-driven element. `options` built
                // from orgForms.data; DS renders the label by value so
                // the base-ui SelectValue function-child id→label fix is
                // no longer needed. `onValueChange` → `onChange`.
                // `className="w-full"` keeps the full-width audit fix.
                <Select
                  className="w-full"
                  placeholder="Pick a form template…"
                  value={pickedFormId}
                  options={(orgForms.data ?? []).map((f) => ({
                    label: f.formName ?? f.name ?? '—',
                    value: (f.id as string) ?? '',
                  }))}
                  onChange={(v) => {
                    const id = v ?? '';
                    setPickedFormId(id);
                    // Selecting a template auto-downloads the blank form so
                    // the user can fill + re-upload it (Andrew 2026-06-10).
                    const picked = (orgForms.data ?? []).find(
                      (f) => f.id === id,
                    );
                    const url = resolveFormTemplateUrl(picked);
                    if (url) {
                      void downloadCrossOrigin(
                        url,
                        resolveFormTemplateFilename(picked),
                      );
                    }
                  }}
                />
              )}

              {/* Download link for the picked template. Cross-origin
                  download (Samer review PR #137 §3): the `download`
                  attribute is IGNORED by browsers when the href is
                  cross-origin, and Azure-blob URLs always are. The
                  button below fetches the blob → ObjectURL → triggers
                  a synthetic anchor click so the file actually
                  downloads with the right name instead of navigating
                  to the blob URL inline (which opens PDFs in the
                  browser viewer and force-renders DOCs as raw XML).
                  Fallback: if fetch fails (CORS, network, expired
                  SAS), open the URL in a new tab as a last resort. */}
              {resolveFormTemplateUrl(pickedTemplate) ? (
                <button
                  type="button"
                  onClick={() =>
                    downloadCrossOrigin(
                      resolveFormTemplateUrl(pickedTemplate) as string,
                      resolveFormTemplateFilename(pickedTemplate),
                    )
                  }
                  className="mt-1 inline-flex items-center gap-1 text-sm text-brand-primary hover:underline"
                >
                  <Download className="size-4" aria-hidden />
                  Download template
                </button>
              ) : null}
            </div>
          ) : null}

          {/* Name — Procedure / Radiology types use a code picker
              instead of a free-text name input. The picked code's
              display string ships as both `name` and `data.title` on
              the wire (Flutter parity). */}
          {isCodeDrivenType ? (
            <div className="space-y-2">
              <label className="text-xs font-bold uppercase tracking-wide text-text-hint">
                {docType === DocumentType.Procedure
                  ? 'Procedure name'
                  : 'Radiology name'}{' '}
                <span className="text-status-error">*</span>
              </label>
              <CodePicker
                kind={
                  docType === DocumentType.Procedure ? 'procedure' : 'radiology'
                }
                value={pickedCode}
                onChange={setPickedCode}
              />
            </div>
          ) : docType === DocumentType.Forms ? null : (
            <div className="space-y-2">
              <label className="text-xs font-bold uppercase tracking-wide text-text-hint">
                {/* PROD-905: Notes type ships verbatim Flutter label
                    "Reason for Visit" + placeholder "Enter reason for
                    visit" (`note_document_widget.dart:65, :76`). All
                    other unlocked types keep "Name". Locked-mode (Lab
                    Linkage) keeps "File Name". Admin Referrals retain
                    "Name" — Flutter has TWO inputs there (File Name +
                    Reason); the full 2-input port is a separate
                    ticket. */}
                {lockedType
                  ? 'File Name'
                  : docType === DocumentType.Notes
                    ? 'Reason for Visit'
                    : docType === DocumentType.Labs ||
                        docType === DocumentType.AdminReferrals
                      ? 'File name'
                      : 'Name'}{' '}
                <span className="text-status-error">*</span>
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                // PROD-906: Flutter caps the name field at 50 chars
                // (`note_document_widget.dart:61`). Mirror so users
                // don't learn the limit on save.
                maxLength={50}
                placeholder={
                  lockedType
                    ? 'File name'
                    : docType === DocumentType.Notes
                      ? 'Enter reason for visit'
                      : docType === DocumentType.Labs ||
                          docType === DocumentType.AdminReferrals
                        ? 'Enter file name'
                        : 'Document name'
                }
                className="w-full h-10 px-2 rounded-input border border-surface-background bg-surface-background text-body-15 text-text-primary placeholder:text-body-15 placeholder:text-text-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary"
              />
            </div>
          )}

          {/* Document date — label changes per type to mirror Flutter
              ("procedure date" / "imaging date" / "lab test date" /
              "document date"). */}
          <div className="space-y-2">
            <label className="text-xs font-bold uppercase tracking-wide text-text-hint">
              {docType === DocumentType.Procedure
                ? 'Procedure date'
                : docType === DocumentType.Radiology
                  ? 'Imaging date'
                  : docType === DocumentType.Labs
                    ? 'Lab test date'
                    : docType === DocumentType.Forms
                      ? 'Sign date'
                      : docType === DocumentType.Notes
                        ? 'Note date'
                        : 'Date'}{' '}
              <span className="text-status-error">*</span>
            </label>
            <DatePicker
              value={ymdToLocalDate(documentDate)}
              onChange={(d) => setDocumentDate(dateToYmd(d))}
              // Zero-padded day, matching Flutter's app-wide `scDateFormat`
              // default `'MMM dd, yyyy'` (scrips_core/lib/utils/utils.dart:176)
              // — deployed renders "Sep 08, 2026". The DS default is date-fns
              // `'PP'`, which gives "Sep 8, 2026". Same format string as
              // `formatScDate` above, so the Lab auto-name and this field agree.
              // OWED: every other date in the PA still renders `'PP'`; making
              // this app-wide is a separate decision, not mine to take here.
              dateFormat="MMM dd, yyyy"
            />
          </div>

          {/* Optional metadata.
              Locked-mode (Lab Linkage) hides Doctor name + Tags per the
              Flutter design — only Facility is surfaced. */}
          {lockedType ? (
            <div className="space-y-2">
              <label className="text-xs font-bold uppercase tracking-wide text-text-hint">
                Facility Name
              </label>
              <input
                type="text"
                value={facilityName}
                onChange={(e) => setFacilityName(e.target.value)}
                // PROD-906: Flutter `note_document_widget.dart:150` cap.
                maxLength={50}
                // Flutter `note_document_widget.dart:165`.
                placeholder="Enter facility name"
                className="w-full h-10 px-2 rounded-input border border-surface-background bg-surface-background text-body-15 text-text-primary placeholder:text-body-15 placeholder:text-text-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary"
              />
            </div>
          ) : (
            <>
              {/* Doctor name — Flutter renders it ONLY on the Note sub-form
                  (`note_document_widget.dart:104`). Lab / Form / Procedure /
                  Radiology / (non-beta) Admin have NO doctor field. */}
              {docType === DocumentType.Notes ? (
                <div className="space-y-2">
                  <label className="text-xs font-bold uppercase tracking-wide text-text-hint">
                    Doctor name
                  </label>
                  <input
                    type="text"
                    value={doctorName}
                    onChange={(e) => setDoctorName(e.target.value)}
                    // PROD-906: Flutter `note_document_widget.dart:122` cap.
                    maxLength={50}
                    // Flutter `note_document_widget.dart:137`.
                    placeholder="Enter doctor name"
                    className="w-full h-10 px-2 rounded-input border border-surface-background bg-surface-background text-body-15 text-text-primary placeholder:text-body-15 placeholder:text-text-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary"
                  />
                </div>
              ) : null}
              {/* Facility name — every type EXCEPT Form (Flutter Form has no
                  facility field; `forms_document_widget.dart` renders only
                  the form-name dropdown + sign date). */}
              {docType !== DocumentType.Forms ? (
                <div className="space-y-2">
                  <label className="text-xs font-bold uppercase tracking-wide text-text-hint">
                    Facility name
                  </label>
                  <input
                    type="text"
                    value={facilityName}
                    onChange={(e) => setFacilityName(e.target.value)}
                    // PROD-906: Flutter `note_document_widget.dart:150` cap.
                    maxLength={50}
                    // Flutter `note_document_widget.dart:165`.
                    placeholder="Enter facility name"
                    className="w-full h-10 px-2 rounded-input border border-surface-background bg-surface-background text-body-15 text-text-primary placeholder:text-body-15 placeholder:text-text-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary"
                  />
                </div>
              ) : null}
              {/* No Tags input — Flutter's New File popup has no tags field
                  on any sub-form (audit 2026-06-09). The `tags` value still
                  round-trips in edit mode (hydrated from the existing doc),
                  so editing a doc that already has tags doesn't wipe them. */}
            </>
          )}

          {/* Lab rows — only renders for Lab type. Each row needs a
              picked lab + result + unit to count as valid; rows still
              empty when the user clicks Save are silently dropped.
              Locked-Lab (Lab Linkage) layout matches Flutter:
                • Section heading + subtitle (no red asterisk —
                  structured rows are OPTIONAL when uploading a result)
                • Starts with zero rows; the "Add Another Lab" button
                  is the entry point
                • Top border separator above the section */}
          {docType === DocumentType.Labs ? (
            <div className="space-y-2 pt-4 border-t border-border-default">
              {/* Order-Requests (locked) lab UI on every surface
                  (Andrew 2026-06-10): heading + subtitle, zero-row start,
                  card/editor rows, "Add Another Lab". */}
              <div className="space-y-1">
                <label className="block font-bold uppercase tracking-wide text-text-hint text-xs">
                  Lab Test Results
                </label>
                <p className="text-sm text-text-muted">
                  You can add lab test results one by one.
                </p>
              </div>
              <div className="space-y-3">
                {labRows.map((entry) => {
                    // Each row toggles between a compact read-only card
                    // (lab name + unit + edit/delete) and a full editor
                    // (lab picker, result + unit, Done/Cancel). Only one
                    // row edits at a time — "Add Another Lab" hides while
                    // any row is editing.
                      if (entry.isEditing) {
                        const labCodeFilled = !!entry.row.labName?.code;
                        const allFilled =
                          labCodeFilled &&
                          !!entry.row.result?.trim() &&
                          !!entry.row.unitType?.code;
                        return (
                          <div
                            key={entry.key}
                            className="space-y-4 rounded-card border border-border-default p-4 bg-surface-card"
                          >
                            <div className="space-y-2">
                              <label className="block text-xs font-bold uppercase tracking-wide text-text-hint">
                                Lab Test Name{' '}
                                <span className="text-status-error">*</span>
                              </label>
                              <LabNamePicker
                                value={entry.row.labName ?? null}
                                onChange={(labName) =>
                                  setLabRows((prev) =>
                                    prev.map((r) =>
                                      r.key === entry.key
                                        ? {
                                            ...r,
                                            row: {
                                              ...r.row,
                                              labName: labName ?? undefined,
                                            },
                                          }
                                        : r,
                                    ),
                                  )
                                }
                                compact
                              />
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                              <div className="space-y-2">
                                <label className="block text-xs font-bold uppercase tracking-wide text-text-hint">
                                  Result
                                </label>
                                <input
                                  type="text"
                                  inputMode="decimal"
                                  value={entry.row.result ?? ''}
                                  onChange={(e) =>
                                    setLabRows((prev) =>
                                      prev.map((r) =>
                                        r.key === entry.key
                                          ? {
                                              ...r,
                                              row: {
                                                ...r.row,
                                                result: e.target.value,
                                              },
                                            }
                                          : r,
                                      ),
                                    )
                                  }
                                  placeholder="Enter result"
                                  className="h-10 w-full px-2 rounded-input border border-surface-background bg-surface-background text-body-15 text-text-primary placeholder:text-body-15 placeholder:text-text-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary"
                                />
                              </div>
                              <div className="space-y-2">
                                <label className="block text-xs font-bold uppercase tracking-wide text-text-hint">
                                  Unit
                                </label>
                                <UnitPicker
                                  value={entry.row.unitType ?? null}
                                  onChange={(unitType) =>
                                    setLabRows((prev) =>
                                      prev.map((r) =>
                                        r.key === entry.key
                                          ? {
                                              ...r,
                                              row: {
                                                ...r.row,
                                                unitType: unitType ?? undefined,
                                              },
                                            }
                                          : r,
                                      ),
                                    )
                                  }
                                  compact
                                />
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              {/* Done — Flutter `enableSave` gate:
                                  only requires the lab name to be
                                  picked (result + unit can be filled
                                  later). */}
                              <button
                                type="button"
                                disabled={!labCodeFilled}
                                onClick={() =>
                                  setLabRows((prev) =>
                                    prev.map((r) =>
                                      r.key === entry.key
                                        ? { ...r, isEditing: false }
                                        : r,
                                    ),
                                  )
                                }
                                className={cn(
                                  'h-8 px-4 rounded-md text-sm font-semibold transition-colors',
                                  labCodeFilled
                                    ? 'bg-brand-primary text-white hover:bg-brand-depth'
                                    : 'bg-grey-100 text-text-hint cursor-not-allowed',
                                )}
                              >
                                Done
                              </button>
                              {/* Cancel — VERBATIM Flutter rule
                                  (lab_document_widget.dart:514-541).
                                  The asymmetry is intentional:
                                    • Partial / empty row → cancel
                                      treats the in-progress add as
                                      "back out", drops the row. The
                                      row has no commit point yet, so
                                      throwing it away is the natural
                                      undo.
                                    • Fully-filled row → cancel is
                                      "stop editing" (the user
                                      already had a valid record;
                                      Done would have closed it too).
                                      Keeping the data avoids
                                      accidentally destroying a
                                      completed entry on a stray
                                      Cancel click.
                                  Matches Flutter byte-for-byte —
                                  changing the rule diverges from PM
                                  parity and would surface in side-
                                  by-side QA. */}
                              <button
                                type="button"
                                onClick={() => {
                                  if (allFilled) {
                                    setLabRows((prev) =>
                                      prev.map((r) =>
                                        r.key === entry.key
                                          ? { ...r, isEditing: false }
                                          : r,
                                      ),
                                    );
                                  } else {
                                    setLabRows((prev) =>
                                      prev.filter((r) => r.key !== entry.key),
                                    );
                                  }
                                }}
                                className="h-8 px-4 rounded-md text-sm font-semibold text-text-primary bg-grey-100 hover:bg-grey-200 transition-colors"
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        );
                      }
                      // Read-only summary card (Flutter `labView`).
                      return (
                        <div
                          key={entry.key}
                          className="flex items-center justify-between rounded-card border border-border-default bg-surface-card px-4 py-3"
                        >
                          <div className="flex flex-col gap-1 min-w-0">
                            <span className="text-base font-bold text-text-primary truncate">
                              {entry.row.labName?.display ?? '—'}
                            </span>
                            {entry.row.unitType?.display ? (
                              <span className="text-sm font-semibold text-brand-primary">
                                {entry.row.unitType.display}
                              </span>
                            ) : null}
                          </div>
                          <div className="flex items-center gap-3 shrink-0 ml-3">
                            <button
                              type="button"
                              onClick={() =>
                                setLabRows((prev) =>
                                  prev.map((r) =>
                                    r.key === entry.key
                                      ? { ...r, isEditing: true }
                                      : r,
                                  ),
                                )
                              }
                              aria-label="Edit lab row"
                              className="icon-action"
                            >
                              <Pencil className="size-5" aria-hidden />
                            </button>
                            <button
                              type="button"
                              onClick={() =>
                                setLabRows((prev) =>
                                  prev.filter((r) => r.key !== entry.key),
                                )
                              }
                              aria-label="Delete lab row"
                              className="icon-action"
                            >
                              <Trash2 className="size-5" aria-hidden />
                            </button>
                          </div>
                        </div>
                      );
                  })}
              </div>
              {/* "Add Another Lab" — hidden while ANY row is in edit mode
                  (Flutter parity: only one row editable at a time). */}
              {!labRows.some((r) => r.isEditing) ? (
                <button
                  type="button"
                  onClick={() =>
                    setLabRows((prev) => [
                      ...prev,
                      { key: nextLabRowKey(), row: newLabRow(), isEditing: true },
                    ])
                  }
                  className="w-full inline-flex items-center justify-center gap-2 rounded-md border border-border-default bg-surface-card py-2.5 text-base font-bold text-brand-primary hover:bg-blue-100/40 transition-colors"
                >
                  <Plus className="size-4" aria-hidden />
                  Add Another Lab
                </button>
              ) : null}
            </div>
          ) : null}

        </div>

    </Drawer>
  );
}
